import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Button, Checkbox, Form, message, Modal, Spin, Steps,
} from 'antd';
import { useState, useEffect } from 'react';
import { useRequest } from 'ahooks';
import { isEmpty, last } from 'lodash';
import dayjs from 'dayjs';
import BankCard from '@/components/ProductFilter/BankCard';
import './index.less';
import BasicInfoForm from '@/components/FormInfo/BasicInfoForm';
import { getArea, getDictList, getDictType } from '@/services/common';
import {
  BASIC_LEVEL,
  BUSINESS_TYPE,
  BUSINESS_UNIT,
  CQ_BUSINESS_TYPE,
  LOAN_TYPE,
  ORIENTATIO_TYPE,
} from '@/utils/mappingDict';
import { getRegisterBasicType } from '@/services/login';
import FinancingNeeds from '@/components/FormInfo/FinancingNeeds';
import CqBusinessForm from '@/components/FormInfo/CqBusinessForm';
import { addCqAdvanceCredit, getCqCredit, getCqWhite } from '@/services/bank';
import { getToken } from '@/utils/token';
import { getUserBasic } from '@/services/user';
import AuthorizationInfo from '@/components/FormInfo/AuthorizationInfo';

const dictParams = [LOAN_TYPE, BUSINESS_TYPE, BASIC_LEVEL, ORIENTATIO_TYPE, BUSINESS_UNIT, CQ_BUSINESS_TYPE].join(',');

export default function Application() {
  const { state: product } = useLocation();
  const Navigate = useNavigate();
  const { status, userType, userInfo } = useSelector((state) => state);
  const token = getToken();
  const [form] = Form.useForm();
  const [current, setCurrent] = useState(0);
  const [formData, setFormData] = useState({ // 分布表单数据存储
    applicationRequestVo: {},
    applicationUserBasicInfoVo: {},
    cqBusinessInfoVo: undefined,
  });
  const [authInfoModal, setAuthInfoModal] = useState(false); // 授权书内容

  const { data: area = [] } = useRequest(getArea); // 区域
  const { data: dict = {} } = useRequest(getDictType, { // 筛选项字典
    defaultParams: [dictParams],
  });

  const { data: basicType = [] } = useRequest(() => getRegisterBasicType(3)); // 获取主体类型

  const { data: treeList } = useRequest(getDictList, { // 树状列表字典
    defaultParams: ['plantType'],
  });

  // 获取重庆银行白名单情况
  const { loading: cqWhiteLoading, runAsync: cqWhiteRunAsync } = useRequest(getCqWhite, {
    manual: true,
  });
  // 获取重庆银行已授信信息
  const { loading: cqCreditLoading, runAsync: cqCreditRunAsync } = useRequest(getCqCredit, {
    manual: true,
  });

  // 重庆银行申请
  const { loading: addCqLoading, run: addCqRun } = useRequest(addCqAdvanceCredit, {
    manual: true,
    onSuccess(res) {
      const { decisionResult } = res;
      Navigate('/result', { state: { ...res, decisionResult: decisionResult === '01' ? '03' : decisionResult, bankType: 4 } });
    },
  });

  const { loading, run } = useRequest(getUserBasic, {
    manual: true,
    onSuccess(res) {
      form.setFieldsValue({
        ...res?.applicationUserBasicInfoVo,
        basicType: userType === 2 ? undefined : res?.applicationUserBasicInfoVo?.basicType,
        familyName: userType === 3 ? userInfo?.realName : res?.applicationUserBasicInfoVo?.familyName,
      });
    },
  }); // 获取经营基本信息

  useEffect(() => {
    if (token && !status) {
      run();
    }
  }, []);

  // 重庆银行白名单，授信情况查看
  function fetchCqCredit(values) {
    const {
      basicNo, familyName, basicCard, basicTel,
    } = values;
    cqWhiteRunAsync({
      basicNo, familyName, basicCard, basicTel,
    }).then((res) => {
      if (res && res.state !== '01') {
        const str = res.state === '02' ? '非白名单客户不可申请' : '法人姓名不一致';
        message.error(`${str}，请联系客户经理`);
      } else {
        // 获取授信额度
        cqCreditRunAsync({ familyName, basicCard }).then((r) => {
          if (!r || isEmpty(r?.datasArray)) {
            setCurrent(current + 1);
          } else {
            const creditParams = last(r?.datasArray);
            const { decisionResult, dueDate } = creditParams;
            // 如果已授信，判断是否过期，过期了可继续申请
            const now = dayjs(); // 当前日期
            const target = dayjs(dueDate); // 目标日期
            const isExpired = now.isAfter(target);
            if (decisionResult === '02' || (decisionResult === '01' && isExpired)) {
              setCurrent(current + 1);
            } else {
              Navigate('/result', { state: { ...creditParams, bankType: 4 } });
            }
          }
        });
      }
    });
  }

  function handleSubmit() {
    const { applicationRequestVo, applicationUserBasicInfoVo } = formData;
    form.validateFields().then((values) => {
      const val = {
        blankListProducts: {
          productId: product?.productId,
          productName: product?.productName,
          publishOrgId: product?.publishOrgId,
          publishOrgName: product?.publishOrgName,
        },
        applicationRequestVo,
        applicationUserBasicInfoVo: {
          ...applicationUserBasicInfoVo,
          basicProvicnce: applicationUserBasicInfoVo.basicProvicnce[0],
          basicArea: applicationUserBasicInfoVo.basicProvicnce[2],
          basicCity: applicationUserBasicInfoVo.basicProvicnce[1],
        },
        cqBusinessInfoVo: {
          ...values,
          plantType: values?.plantType?.[1],
        },
      };
      addCqRun(val);
    });
  }

  function handlePrev() {
    setCurrent(current - 1);
  }

  function handleNext() {
    form.validateFields().then((values) => {
      if (current === 0) {
        fetchCqCredit(values);
      } else {
        setCurrent(current + 1);
      }
      const val = current === 0 ? { ...formData, applicationUserBasicInfoVo: values } : { ...formData, applicationRequestVo: values };
      setFormData(val);
    });
  }

  function checkAuthorize(_, value) {
    const promise = Promise;
    if (!value) {
      return promise.reject('请勾选同意协议');
    }
    return promise.resolve();
  }

  return (
    <div className="cq-pro-application">
      <div className="content-width padding-top40">
        <BankCard data={product} isShow />
        <div className="cq-pro-application__title">产品申请</div>
        <div className="cq-pro-application__con">
          <div className="cq__steps">
            <Steps current={current} type="navigation">
              <Steps.Step title="步骤一" description="基础信息" />
              <Steps.Step title="步骤二" description="金融需求信息" />
              <Steps.Step title="步骤三" description="经营信息" />
            </Steps>
            <Spin spinning={loading || addCqLoading}>
              <Form
                form={form}
                name="basic"
                className="cq__form"
                labelCol={{
                  span: 7,
                }}
                wrapperCol={{
                  span: 16,
                }}
                autoComplete="off"
              >
                {
                  current === 0 && (
                    <BasicInfoForm options={area} dict={{ ...dict, basicType, product }} />
                  )
                }
                {
                  current === 1 && (
                    <FinancingNeeds data={{ ...dict, product }} type="yhzd" form={form} isHide />
                  )
                }
                {
                  current === 2 && (
                    <CqBusinessForm dict={{ ...dict, ...treeList }} formRef={form} />
                  )
                }
                {
                  current === 2 && (
                    <div className="authorize">
                      <Form.Item
                        name="authorize"
                        wrapperCol={{ span: 24 }}
                        valuePropName="checked"
                        rules={[
                          { validator: checkAuthorize },
                        ]}

                      >
                        <Checkbox>
                          同意并接受
                          <Button
                            type="link"
                            className="authorize-name"
                            onClick={() => setAuthInfoModal(true)}
                          >
                            《直通车平台业务授权协议》
                          </Button>
                        </Checkbox>
                      </Form.Item>
                    </div>
                  )
                }
                <div className="cq__form-btn">
                  <Button type="primary" ghost onClick={handlePrev} hidden={current === 0}>上一步</Button>
                  <Button
                    type="primary"
                    onClick={handleNext}
                    loading={cqWhiteLoading || cqCreditLoading}
                    hidden={current === 2}
                  >
                    下一步
                  </Button>
                  <Button type="primary" className="submit-btn__main" onClick={handleSubmit} hidden={current !== 2}>
                    立即申请
                  </Button>
                </div>
              </Form>
            </Spin>
          </div>
        </div>
      </div>
      <Modal title="授权协议查看" open={authInfoModal} width={900} footer={null} onCancel={() => setAuthInfoModal(false)}>
        <div dangerouslySetInnerHTML={{ __html: AuthorizationInfo }} style={{ maxHeight: 600, overflowY: 'scroll' }} />
      </Modal>

    </div>
  );
}
